import type { IGrid } from "./types";
import type { Behavior } from "./Behavior";

export interface ParticleOptions {
    color: number;
    name?: string;
    empty?: boolean;
    behaviors?: Behavior[];
    props?: Partial<ParticleProps>;
    temperature?: number;
    minTemp?: number;
    maxTemp?: number;
}

export interface ParticleProps {
    isLiquid: boolean;
    isFlammable: boolean;
    isMixable: boolean;
    isPowder: boolean; // 是否为粉末状
    isGlued: boolean; // 是否被胶水固定
    thermalConductivity: number; // 热导率：影响接收热量的速度 (0-1)
    density: number; // 密度：水为1，油<1则上浮，石头>1则下沉 (液体/气体浮力)
}

/**
 * 粒子基类
 * 代表网格中的一个点
 */
export class Particle {
    name: string;
    color: number;
    empty: boolean;
    behaviors: Behavior[];
    behaviorsLookup: Record<string, Behavior>;
    updated: boolean = false; // 防止一帧内多次更新
    props: ParticleProps;
    temperature: number;
    minTemp: number;
    maxTemp: number;
    velocity: { x: number; y: number };

    constructor(options: ParticleOptions) {
        this.name = options.name || "Unknown";
        this.color = options.color;
        this.empty = options.empty ?? false;
        this.behaviors = options.behaviors ?? [];
        this.behaviorsLookup = {};
        this.props = {
            isLiquid: false,
            isFlammable: false,
            isMixable: true, // 默认为 true
            isPowder: false,
            isGlued: false,
            thermalConductivity: 1.0, // 默认为 1.0 (正常导热)
            density: 1.0, // 默认密度为 1.0
            ...options.props
        } as ParticleProps;
        this.temperature = options.temperature ?? 20;
        this.minTemp = options.minTemp ?? -273;
        this.maxTemp = options.maxTemp ?? 10000;
        // 给一个微小的随机初始速度，打破完全同步的下落节奏
        // 这样即使在同一帧生成，也会因为初始速度微小差异而在几帧后拉开不同距离
        // 注意不要太大，否则会感觉生成时会"跳"一下
        // 增加一点范围，有助于模糊快速绘制时的帧间断层
        this.velocity = { x: 0, y: Math.random() * 1.0 };
        
        for (const b of this.behaviors) {
            this.behaviorsLookup[b.constructor.name] = b;
        }
    }

    /**
     * 更新粒子
     * @param grid 网格
     * @param index 当前索引
     */
    update(grid: IGrid, index: number) {
        if (this.updated) return;
        
        let currentIndex = index;
        for (const behavior of this.behaviors) {
            // 如果被胶水固定，跳过被标记为 suppressByGlue 的行为
            if (this.props.isGlued && behavior.suppressByGlue) {
                continue;
            }

            const result = behavior.update(this, grid, currentIndex);
            if (result === -1) {
                // 粒子被销毁，停止后续行为
                return;
            }
            if (typeof result === 'number') {
                currentIndex = result;
            }
        }
        this.updated = true;
    }

    /**
     * 获取指定类型的行为实例
     * @param type 行为类
     */
    getBehavior<T extends Behavior>(type: new (...args: any[]) => T): T | undefined {
        return this.behaviorsLookup[type.name] as T;
    }
}
